import { useQuery, NetworkStatus } from '@apollo/client'
import { isEmpty } from 'lodash'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { Loader } from '@10xLabs/react-web/components/Loader'
import EmptyState from 'components/v2/EmptyState'
import Heading from 'components/v2/Heading'
import { Content, ContentBody, ContentHeader } from 'components/v2/Content'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { canRead } from 'features/Users/utils'
import { useLogin } from 'features/Login/hooks'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { MAX_INT_FOR_PAGINATION_SIZE } from 'config/constants'
import { GET_ROUTES_PLANNING } from 'features/RoutesPlanning/queries'
import { createRoutesPlanningFiltersModel } from 'features/RoutesPlanning/models'
import { useBusinessClientAutocomplete } from 'features/BusinessClients/hooks'
import { useBusinessSiteAutocomplete } from 'features/BusinessSites/hooks'
import { getQueries, getQueryString } from 'utils/route'
import { RoutesPlanningMap } from 'features/RoutesPlanning/components/RoutesPlanningMap'
import { BusinessClientSuggestion, CompanySuggestion, TRoute } from 'gql/generated/graphql'
import { createRoutesPlanningCollection } from 'features/RoutesPlanning/collections'
import { GTRouteSuggestionsResponse } from 'gql/types.generated'
import { GET_ROUTES_SUGGESTIONS } from 'features/Routes/queries-legacy'
import { RoutesPlanningList } from 'features/RoutesPlanning/views/Index/List'
import { neutralTheme, RoutePlanningEmptyState } from 'features/RoutesPlanning/components/EmptyState'

import { RoutesListActions } from 'features/RoutesPlanning/views/Index/List/Actions'
import { useGoogleMapsScript } from 'hooks/google-maps'
import { RoutePlanningMainFilters } from 'features/RoutesPlanning/views/Index/MainFilters'
import { useCompanyAutocomplete } from 'features/Companies/hooks'

const StyledContent = styled(Content)`
  height: calc(100% - 60px);
  padding: 30px 20px;
  display: grid;
  grid-template-rows: min-content 1fr;
`

const StyledContentBody = styled(ContentBody)`
  height: 0;
  min-height: 100%;
`

const Section = styled.section`
  height: 100%;
`

const RoutesGrid = styled.div<{ isRoutesListExpanded: boolean }>`
  height: 100%;
  display: ${({ isRoutesListExpanded }): string => (isRoutesListExpanded ? 'grid' : 'block')};
  grid-template-columns: 392px minmax(500px, 1fr);
  border-radius: 16px;
  overflow: hidden;
  position: relative;
`

const RoutesListContainer = styled.div<{ isExpanded: boolean }>`
  padding: 16px;
  height: ${({ isExpanded }): string => (isExpanded ? 'calc(100% - 32px);' : 'min-content')};
  display: grid;
  grid-template-rows: min-content ${({ isExpanded }): string => (isExpanded ? '1fr' : '')};
  gap: 20px;
  position: ${({ isExpanded }): string => (isExpanded ? 'relative' : 'absolute')};
  z-index: ${({ isExpanded }): string => (isExpanded ? '0' : '1')};
  background-color: #fff;
  top: ${({ isExpanded }): string => (isExpanded ? '0' : '24px')};
  left: ${({ isExpanded }): string => (isExpanded ? '0' : '13px')};
  border-radius: 16px;
  min-width: 360px;
`

const RoutesListHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`

const RoutesContent = styled.div`
  height: 0;
  min-height: 100%;
  overflow: auto;
`

const RoutesPlanningMapContainer = styled.div`
  height: 100%;
`

const messages = defineMessages({
  helmet: {
    id: 'routesPlanning.helmet',
    defaultMessage: 'Routes planning | Nexbus Admin'
  }
})

const RoutesPlanningView = (): ReactElement => {
  let content
  const location = useLocation()
  const intl = useIntl()
  const searchQuery = getQueries(location.search)
  const companyId = searchQuery.companyId || ''
  const businessClientId = searchQuery.businessClientId || ''
  const businessSiteId = searchQuery.businessSiteId || ''
  const offerDateStartAt = searchQuery.offerDateStartAt || ''
  const offerDateEndAt = searchQuery.offerDateEndAt || ''
  const routesIdsList = searchQuery.routesIds ? searchQuery.routesIds.split(',') : []
  const login = useLogin()
  const navigate = useNavigate()
  const [businessClient, setBusinessClient] = useState<BusinessClientSuggestion | null>(null)
  const [company, setCompany] = useState<CompanySuggestion | null>(null)
  const [visibleRoutesIds, setVisibleRoutesIds] = useState<string[]>([])
  const [isRoutesListExpanded, setIsRoutesListExpanded] = useState(true)
  const { isLoaded: isGoogleMapsLoaded, geometryLib } = useGoogleMapsScript()

  const businessClientAutocomplete = useBusinessClientAutocomplete(businessClientId, { skip: !businessClientId })
  useEffect(() => {
    if (!businessClientId) {
      setBusinessClient(null)
    } else if (businessClientAutocomplete) {
      setBusinessClient(businessClientAutocomplete)
    }
  }, [businessClientAutocomplete, businessClient, businessClientId])
  const companyAutocomplete = useCompanyAutocomplete(companyId, { skip: !companyId })
  useEffect(() => {
    if (!companyId) {
      setCompany(null)
    } else if (companyAutocomplete) {
      setCompany(companyAutocomplete)
    }
  }, [companyAutocomplete, companyId, company])
  const businessSiteAutocomplete = useBusinessSiteAutocomplete(businessSiteId, { skip: !businessSiteId })
  const { data: routesData } = useQuery<{ routeSuggestions: GTRouteSuggestionsResponse }>(GET_ROUTES_SUGGESTIONS, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: isEmpty(routesIdsList),
    variables: {
      input: {
        ids: routesIdsList
      }
    }
  })
  const filters = createRoutesPlanningFiltersModel(
    {
      companyId,
      businessClientId,
      businessSiteId,
      offerDateStartAt,
      offerDateEndAt,
      routesIds: searchQuery.routesIds
    },
    {
      businessSiteAutocomplete,
      routesAutocomplete: routesData?.routeSuggestions.items || []
    }
  )
  const { loading, error, data, refetch, networkStatus } = useQuery(GET_ROUTES_PLANNING, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: !businessClientId && !companyId,
    variables: {
      pagination: {
        page: 1,
        size: MAX_INT_FOR_PAGINATION_SIZE
      },
      input: {
        isActive: true,
        ...filters.toQueryInput()
      }
    },
    onCompleted(data) {
      setVisibleRoutesIds(data?.troutes.items.map((route) => route.id))
    }
  })

  useEffect(() => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  const { routesCollection, filteredVisibleRoutes } = useMemo(() => {
    const routesCollection = createRoutesPlanningCollection(
      (data?.troutes.items as TRoute[]) || [],
      visibleRoutesIds,
      geometryLib
    )
    return {
      routesCollection,
      filteredVisibleRoutes: routesCollection.filterVisibleRoutes()
    }
  }, [data, visibleRoutesIds, geometryLib])

  if (!canRead(FEATURES.ROUTE, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.ROUTE} />
  }

  if (!isGoogleMapsLoaded) {
    return (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  }

  if (!businessClientId && !companyId) {
    content = (
      <RoutePlanningEmptyState
        description={
          <FormattedMessage
            id="routesPlanning.empty.businessClient.description"
            defaultMessage="Please select a company and/or a business client to see its routes."
          />
        }
        theme={{
          borderColor: '#423F9A',
          backgroundColor: '#EAEAFF',
          iconColor: '#423F9A'
        }}
      />
    )
  } else if (networkStatus === NetworkStatus.refetch || loading) {
    content = (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (error) {
    content = (
      <RoutePlanningEmptyState
        description={
          <FormattedMessage
            id="routesPlanning.error.description"
            defaultMessage="There was an error while loading your routes"
          />
        }
        action={
          <span
            onClick={(): void => {
              refetch()
            }}
          >
            <FormattedMessage id="action.reload" defaultMessage="Reload" />
          </span>
        }
        theme={{
          borderColor: '#FF627B',
          backgroundColor: '#FFEAED',
          iconColor: '#FF627B'
        }}
      />
    )
  } else if (isEmpty(data?.troutes.items) && filters.hasFiltersApplied()) {
    content = (
      <RoutePlanningEmptyState
        description={
          <FormattedMessage
            id="routesPlanning.empty.filters.description"
            defaultMessage="There was no routes found according to your filters."
          />
        }
        action={
          <span
            onClick={(): void => {
              const queryString = getQueryString({
                businessClientId,
                companyId
              })
              navigate(`/routes-planning?${queryString}`)
            }}
          >
            <FormattedMessage id="empty.filters.action" defaultMessage="Reset filters" />
          </span>
        }
        theme={neutralTheme}
      />
    )
  } else if (isEmpty(data?.troutes.items)) {
    if (businessClientId) {
      content = (
        <RoutePlanningEmptyState
          description={
            <FormattedMessage
              id="routesPlanning.empty.description"
              defaultMessage="This business client has no routes yet"
            />
          }
          theme={neutralTheme}
        />
      )
    } else {
      content = (
        <RoutePlanningEmptyState
          description={
            <FormattedMessage
              id="routesPlanning.empty.company.description"
              defaultMessage="This company has no routes yet"
            />
          }
          theme={neutralTheme}
        />
      )
    }
  } else {
    content = (
      <RoutesPlanningList
        routes={routesCollection}
        visibleRoutesIds={visibleRoutesIds}
        setVisibleRoutesIds={setVisibleRoutesIds}
      />
    )
  }

  return (
    <StyledContent>
      <ContentHeader isResponsive>
        <Heading.H3>
          <FormattedMessage id="routesPlanning.header.label" defaultMessage="Planning" />
        </Heading.H3>
        <RoutePlanningMainFilters
          businessClient={businessClient}
          setBusinessClient={setBusinessClient}
          company={company}
          setCompany={setCompany}
          secondaryFilters={filters}
        />
      </ContentHeader>
      <StyledContentBody noPadding>
        <Section>
          <RoutesGrid isRoutesListExpanded={isRoutesListExpanded}>
            <RoutesListContainer isExpanded={isRoutesListExpanded}>
              <RoutesListHeader>
                <Heading.H3>
                  <FormattedMessage id="routesPlanning.list.title" defaultMessage="Routes" />
                </Heading.H3>
                <RoutesListActions
                  visibleRoutesIds={visibleRoutesIds}
                  setVisibleRoutesIds={setVisibleRoutesIds}
                  isRoutesListExpanded={isRoutesListExpanded}
                  setIsRoutesListExpanded={setIsRoutesListExpanded}
                  allRoutesIds={routesCollection.items.map((route) => route.id)}
                />
              </RoutesListHeader>
              {isRoutesListExpanded && <RoutesContent>{content}</RoutesContent>}
            </RoutesListContainer>
            <RoutesPlanningMapContainer>
              <RoutesPlanningMap routes={filteredVisibleRoutes} />
            </RoutesPlanningMapContainer>
          </RoutesGrid>
        </Section>
      </StyledContentBody>
    </StyledContent>
  )
}

export default withLastUrl(RoutesPlanningView)
